import { ListMagnifyingGlassIcon } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/components/feedback/toast';

import { NOTIFICATION_COPY, NOTIFICATION_POLICY } from '../constants';
import { useMarkNotificationsRead, useNotifications } from '../hooks';
import { notificationDestination } from '../navigation';
import type { Notification } from '../schemas';

import { NotificationRow } from './notification-row';
import { NotificationsPanelSkeleton } from './notifications-panel-skeleton';
import styles from './notifications-panel.module.scss';

export type NotificationsPanelProps = {
  /** Closes the containing dropdown after a row navigates. */
  onClose: () => void;
};

/**
 * The bell dropdown (Figma 621:549480 loaded, 621:552048 empty, 621:554520 loading).
 *
 * Three things happen on open, in this order: the feed is fetched, the listed notifications are
 * marked read (US-101849: "Opening the Notifications screen marks the currently listed
 * notifications as read"), and a load failure raises a toast with **no retry control** — the story
 * rules one out explicitly.
 *
 * Paging is inline scrolling, again per the story: no pager, no "load more" button.
 */
export function NotificationsPanel({ onClose }: NotificationsPanelProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const scroller = useRef<HTMLDivElement | null>(null);

  const { items, groups, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNotifications();
  const markRead = useMarkNotificationsRead();

  /**
   * Fired once per open, as soon as the feed shows anything unread.
   *
   * The endpoint marks **every** unread notification and accepts no ids (see `api.ts`), so there
   * is nothing to accumulate and no reason to re-send: one call settles the whole account. The
   * ref guards a re-render, and the dropdown's `destroyOnHidden` makes each open a fresh mount, so
   * "once per open" is what this actually is.
   *
   * It is keyed on the feed having arrived rather than on mount, because a mount effect runs
   * before the fetch resolves and would mark an account read on a request that then failed.
   */
  const alreadyMarked = useRef(false);
  const markReadFor = markRead.mutate;
  useEffect(() => {
    if (alreadyMarked.current) return;
    if (!items.some((notification) => !notification.read)) return;

    alreadyMarked.current = true;
    markReadFor();
  }, [items, markReadFor]);

  // A load failure is announced once per failure, not once per render.
  useEffect(() => {
    if (isError) toast.error({ title: NOTIFICATION_COPY.LOAD_FAILED });
  }, [isError, toast]);

  const onSelect = (notification: Notification) => {
    onClose();
    navigate(notificationDestination(notification));
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= NOTIFICATION_POLICY.SCROLL_THRESHOLD) {
      void fetchNextPage();
    }
  };

  if (isLoading) {
    return <NotificationsPanelSkeleton />;
  }

  // An error and an empty feed both land here. The error already spoke through a toast, so the
  // panel itself must not also claim "you're all caught up" — that would be a lie about a
  // request that failed.
  if (groups.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.empty}>
          <ListMagnifyingGlassIcon size={24} className={styles.emptyIcon} aria-hidden />
          <p className={styles.emptyTitle}>
            {isError ? NOTIFICATION_COPY.LOAD_FAILED : NOTIFICATION_COPY.EMPTY_TITLE}
          </p>
          {isError ? null : <p className={styles.emptyBody}>{NOTIFICATION_COPY.EMPTY_BODY}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.scroller} ref={scroller} onScroll={onScroll}>
        {groups.map((group) => (
          <section key={group.key} className={styles.group}>
            <h3 className={styles.dateHeader}>{group.label}</h3>
            {group.items.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onSelect={onSelect}
              />
            ))}
          </section>
        ))}
        {isFetchingNextPage ? (
          <p className={styles.loadingMore} role="status">
            Loading more…
          </p>
        ) : null}
      </div>
    </div>
  );
}
